import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import './SosAlertBanner.css';
import { SOS_OUTSIDE_GEOFENCE_TITLE, SOS_POLICE_NUMBERS, formatDistance } from '../../utils/lgBandSos';


/** Red SOS bell with sound waves (banner icon). */
const SosBellIcon = () => (
  <svg className="sos-bell-icon" viewBox="0 0 120 90" fill="none" aria-hidden>
    <g stroke="#f00" strokeWidth="6" strokeLinecap="round">
      <path d="M20 22 A46 46 0 0 0 20 68" />
      <path d="M31 28 A34 34 0 0 0 31 62" />
      <path d="M100 22 A46 46 0 0 1 100 68" />
      <path d="M89 28 A34 34 0 0 1 89 62" />
    </g>
    <rect x="57" y="6" width="6" height="8" rx="1" fill="#f00" />
    <path d="M38 62 V40 a22 22 0 0 1 44 0 V62 H88 V70 H32 V62 Z" fill="#f00" />
    <path d="M50 73 a10 10 0 0 0 20 0 Z" fill="#f00" />
    <text x="60" y="52" textAnchor="middle" fontFamily="Inter, Arial, sans-serif" fontWeight="900" fontSize="19" fill="#fff">SOS</text>
  </svg>
);

const formatSosTime = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' });
};

const formatSince = (iso) => {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return null;
  const m = Math.floor(ms / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.floor(m / 60);
  return `${h} hr ${m % 60} min ago`;
};

/** Centered confirmation dialog for resolving an SOS (portal, above the map). */
const SosResolveDialog = ({ alert, bandId, onConfirm, onCancel }) => {
  const yesRef = useRef(null);
  useEffect(() => {
    yesRef.current?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') onCancel();
      if (e.key === 'Enter') onConfirm();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onConfirm, onCancel]);

  const sosIso = alert.sosAt || alert.timestamp;
  const since = formatSince(sosIso);
  const sosTime = formatSosTime(sosIso);
  const where = alert.outsideGeofence
    ? `Out of the Geofenced Zone${alert.nearestBeach ? ` · Nearest Beach: ${alert.nearestBeach}` : ''}`
    : (alert.geofenceName ? alert.geofenceName : (alert.hasPosition === false ? 'No GPS position (last known shown)' : null));
  const nearestTower = alert.nearestTower
    ? `${alert.nearestTower}${alert.nearestTowerDistanceM != null ? ` · ${formatDistance(alert.nearestTowerDistanceM)}` : ''}`
    : 'NA';

  return createPortal(
    <div className="sos-resolve-backdrop" onClick={onCancel} role="presentation">
      <div
        className="sos-resolve-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="sos-resolve-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sos-resolve-head">
          <span className="sos-resolve-icon" aria-hidden>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 6L9 17l-5-5" />
            </svg>
          </span>
          <div>
            <h3 id="sos-resolve-title" className="sos-resolve-title">Has the SOS been resolved?</h3>
          </div>
        </div>

        <div className="sos-resolve-facts">
          <div className="sos-resolve-fact">
            <span className="sos-resolve-fact-label">LG Band</span>
            <span className="sos-resolve-fact-value">{bandId}</span>
          </div>
          {(sosTime || since) && (
            <div className="sos-resolve-fact">
              <span className="sos-resolve-fact-label">Raised</span>
              <span className="sos-resolve-fact-value">{sosTime}{sosTime && since ? ' · ' : ''}{since}</span>
            </div>
          )}
          {where && (
            <div className="sos-resolve-fact">
              <span className="sos-resolve-fact-label">Location</span>
              <span className={`sos-resolve-fact-value${alert.outsideGeofence ? ' sos-resolve-fact-value--warn' : ''}`}>{where}</span>
            </div>
          )}
          <div className="sos-resolve-fact">
            <span className="sos-resolve-fact-label">Nearest Tower</span>
            <span className={`sos-resolve-fact-value${alert.nearestTower ? '' : ' sos-resolve-fact-value--muted'}`}>{nearestTower}</span>
          </div>
        </div>

        <div className="sos-resolve-actions">
          <button type="button" className="sos-resolve-btn sos-resolve-btn--cancel" onClick={onCancel}>
            Not yet
          </button>
          <button type="button" className="sos-resolve-btn sos-resolve-btn--confirm" ref={yesRef} onClick={onConfirm}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M20 6L9 17l-5-5" />
            </svg>
            Yes, resolved
          </button>
        </div>
        <p className="sos-resolve-hint">Enter to confirm · Esc to go back</p>
      </div>
    </div>,
    document.body
  );
};

const SosAlertBanner = ({ alert, onDismiss, onLocate }) => {
  const [confirming, setConfirming] = useState(false);
  const [, setTick] = useState(0);
  useEffect(() => { const id = setInterval(() => setTick((t) => t + 1), 30 * 1000); return () => clearInterval(id); }, []);

  // A different SOS (or none) replaces any pending confirmation.
  useEffect(() => { setConfirming(false); }, [alert?.id]);

  if (!alert) {
    return null;
  }

  const bandId = alert.lgBandId || alert.message?.split(':').pop()?.trim() || 'Unknown';
  const bannerIso = alert.sosAt || alert.timestamp;
  const bannerTime = formatSosTime(bannerIso);
  const bannerSince = formatSince(bannerIso);
  const locate = () => { if (onLocate) onLocate(alert.deviceId, alert); };

  return (
    <div
      className="sos-alert-banner-root"
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
    >
      <div className="sos-alert-banner-pulse-wrap">
        <span className="sos-alert-banner-glow-inner" aria-hidden />
        <span className="sos-alert-banner-glow-outer" aria-hidden />
        <div
          className={`sos-alert-banner-card${onLocate ? ' sos-alert-banner-card--clickable' : ''}`}
          role={onLocate ? 'button' : undefined}
          tabIndex={onLocate ? 0 : undefined}
          title={onLocate ? 'Click to locate this LG Band on the map' : undefined}
          onClick={locate}
          onKeyDown={(e) => {
            if (onLocate && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); locate(); }
          }}
        >
          <div className="sos-alert-banner-icon" aria-hidden>
            <SosBellIcon />
          </div>

          <div className="sos-alert-banner-body">
            {alert.outsideGeofence ? (
              <>
                <p className="sos-alert-banner-text">{SOS_OUTSIDE_GEOFENCE_TITLE}</p>
                <p className="sos-alert-banner-line">Nearest Beach: <strong>{alert.nearestBeach || 'Unknown'}</strong></p>
                <p className="sos-alert-banner-line">
                  Call Police :{' '}
                  {SOS_POLICE_NUMBERS.map((n, i) => (
                    <React.Fragment key={n.number}>
                      {i > 0 ? ' · ' : ''}
                      <a href={`tel:${n.number}`} className="sos-alert-banner-number" onClick={(e) => e.stopPropagation()} title={n.label}>{n.number}</a>
                    </React.Fragment>
                  ))}
                </p>
              </>
            ) : (
              <p className="sos-alert-banner-text">
                SOS Initiated: LG Band: <strong>{bandId}</strong>
              </p>
            )}
            <p className="sos-alert-banner-meta">
              {alert.outsideGeofence && <span>LG Band <strong>{bandId}</strong></span>}
              {bannerTime && <span>Raised <strong>{bannerTime}</strong>{bannerSince ? ` (${bannerSince})` : ''}</span>}
              <span>Nearest Tower: <strong>{alert.nearestTower || 'NA'}</strong>{alert.nearestTower && alert.nearestTowerDistanceM != null ? ` · ${formatDistance(alert.nearestTowerDistanceM)}` : ''}</span>
            </p>
            {alert.hasPosition === false && (
              <p className="sos-alert-banner-hint">No GPS position in this SOS — last known location shown</p>
            )}
            {onLocate && !confirming && <p className="sos-alert-banner-hint">Click to locate on map</p>}

            {confirming && (
              <SosResolveDialog
                alert={alert}
                bandId={bandId}
                onConfirm={() => { setConfirming(false); onDismiss(alert.id, alert); }}
                onCancel={() => setConfirming(false)}
              />
            )}
          </div>

          <div className="sos-alert-banner-actions" onClick={(e) => e.stopPropagation()}>
            {onLocate && (
              <button
                type="button"
                className="sos-alert-banner-locate"
                onClick={(e) => { e.stopPropagation(); locate(); }}
                aria-label="Locate LG Band on map"
              >
                Locate
              </button>
            )}
            <button
              type="button"
              className="sos-alert-banner-resolve"
              onClick={(e) => { e.stopPropagation(); setConfirming(true); }}
              aria-label="Resolve SOS"
              title="Mark this SOS as resolved (clears it for all Atlas users)"
              disabled={confirming}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <path d="M20 6L9 17l-5-5" />
              </svg>
              Resolve
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SosAlertBanner;
